import React, { useState, useRef, useEffect } from "react";
import { Plus, FolderOpen, Settings2, MessageCircle, ChevronDown, Check, LogOut, Pencil, Lock, Sparkles, Crown, CreditCard, Package } from "lucide-react";
import SiteThemeToggle from "./SiteThemeToggle";
import basiqLogo from "../assets/basiq.png";
import basiqMark from "../assets/basiq-mark.png";
import { PLAN_META } from "../lib/plans";

const tint = (hex, a) => {
  if (!hex) return `rgba(15,23,42,${a})`;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
};

function useOutside(ref, cb) {
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) cb(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [ref, cb]);
}

const CompanyAvatar = ({ company, size = 22 }) => {
  const bg = company?.theme?.avatarBg;
  if (company?.logo) {
    return (
      <span className="grid place-items-center rounded-md ring-1 ring-slate-200 overflow-hidden shrink-0" style={{ width: size, height: size, background: bg ? tint(bg, 0.16) : "#fff" }}>
        <img src={company.logo} alt="" className="object-contain" style={{ width: size * 0.78, height: size * 0.78 }} />
      </span>
    );
  }
  return (
    <span className="grid place-items-center rounded-md text-white font-semibold shrink-0" style={{ width: size, height: size, background: bg || "#0f172a", fontSize: size * 0.5 }}>
      {(company?.name || "?").slice(0, 1).toUpperCase()}
    </span>
  );
};

export default function TopNav({ view, onNav, user, companies = [], activeCompanyId, onSwitchCompany, onNewCompany, onRenameCompany, onLogout, planName = "free", subscription, waLocked = false }) {
  const [openCo, setOpenCo] = useState(false);
  const [openUser, setOpenUser] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const coRef = useRef(null);
  const userRef = useRef(null);
  useOutside(coRef, () => { setOpenCo(false); setRenamingId(null); });
  useOutside(userRef, () => setOpenUser(false));

  const commitRename = (id) => {
    const v = renameVal.trim();
    if (v) onRenameCompany && onRenameCompany(id, v);
    setRenamingId(null);
  };

  const active = companies.find((c) => c.id === activeCompanyId);
  const authed = !!user;

  const fmtDate = (sec) => {
    if (!sec) return "";
    try { return new Date(sec * 1000).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" }); } catch { return ""; }
  };
  let subLine = "";
  if (planName !== "free" && subscription) {
    if (subscription.status === "trialing" && subscription.trial_end) subLine = `Teste grátis até ${fmtDate(subscription.trial_end)}`;
    else if (subscription.cancel_at_period_end && subscription.current_period_end) subLine = `Cancela em ${fmtDate(subscription.current_period_end)}`;
    else if (subscription.current_period_end) subLine = `Renova em ${fmtDate(subscription.current_period_end)}`;
  }

  const link = (key, label, Icon, locked = false) => (
    <button
      onClick={() => onNav(key)}
      data-testid={`nav-${key}`}
      className={`shrink-0 inline-flex items-center gap-1.5 text-[13px] font-medium px-3 py-1.5 rounded-full transition-colors ${
        view === key ? "text-slate-900 bg-slate-100" : "text-slate-500 hover:text-slate-800"
      }`}
    >
      <Icon size={15} strokeWidth={2} />
      <span className="hidden md:inline">{label}</span>
      {locked && <Lock size={11} className="text-slate-400" data-testid={`nav-${key}-lock`} />}
    </button>
  );

  return (
    <header className="fixed top-0 inset-x-0 z-40 h-14 flex items-center justify-between px-4 sm:px-6 bg-[#fafafa]/80 backdrop-blur-md border-b border-slate-200/60">
      <div className="flex items-center gap-3 min-w-0">
        <button onClick={() => authed && onNav("home")} className="flex items-center gap-2" data-testid="brand-logo">
          <img src={basiqMark} alt="" className="h-[21px] w-auto object-contain" />
          <img src={basiqLogo} alt="Basiq" className="h-[18px] w-auto object-contain hidden sm:block" />
        </button>

        {authed && active && (
          <div className="relative" ref={coRef}>
            <button
              onClick={() => setOpenCo((s) => !s)}
              data-testid="company-switcher"
              className="ml-1 inline-flex items-center gap-2 h-8 pl-1.5 pr-2 rounded-full bg-white ring-1 ring-slate-200 hover:ring-slate-300 transition-all max-w-[180px]"
            >
              <CompanyAvatar company={active} />
              <span className="text-[13px] font-medium text-slate-800 truncate">{active.name || "Empresa"}</span>
              <ChevronDown size={14} className={`text-slate-400 transition-transform ${openCo ? "rotate-180" : ""}`} />
            </button>
            {openCo && (
              <div className="absolute left-0 mt-2 w-64 bg-white ring-1 ring-slate-200 rounded-2xl p-1.5 shadow-xl z-50" data-testid="company-menu">
                <div className="px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Suas empresas</div>
                <div className="max-h-64 overflow-y-auto scrollbar-none">
                  {companies.map((c) => (
                    <div key={c.id} className={`group/row flex items-center gap-1 rounded-xl pr-1 ${c.id === activeCompanyId ? "bg-slate-100" : "hover:bg-slate-50"}`}>
                      {renamingId === c.id ? (
                        <div className="flex items-center gap-1.5 flex-1 min-w-0 px-2.5 py-1.5">
                          <CompanyAvatar company={c} size={26} />
                          <input
                            autoFocus
                            value={renameVal}
                            onChange={(e) => setRenameVal(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") commitRename(c.id); if (e.key === "Escape") setRenamingId(null); }}
                            data-testid={`rename-input-${c.id}`}
                            className="flex-1 min-w-0 text-[13px] font-medium bg-white ring-1 ring-slate-300 rounded-lg px-2 py-1 outline-none focus:ring-slate-900"
                          />
                          <button onClick={() => commitRename(c.id)} data-testid={`rename-save-${c.id}`} title="Salvar" className="grid place-items-center w-7 h-7 rounded-lg text-emerald-600 hover:bg-emerald-50 shrink-0"><Check size={15} strokeWidth={3} /></button>
                        </div>
                      ) : (
                        <>
                          <button
                            onClick={() => { onSwitchCompany(c.id); setOpenCo(false); }}
                            data-testid={`company-option-${c.id}`}
                            className="flex-1 min-w-0 flex items-center gap-2.5 px-2.5 py-2 text-left"
                          >
                            <CompanyAvatar company={c} size={26} />
                            <span className="flex-1 text-[13px] font-medium text-slate-800 truncate">{c.name || "Empresa"}</span>
                            {c.id === activeCompanyId && <Check size={15} strokeWidth={3} className="text-slate-900 shrink-0" />}
                          </button>
                          <button
                            onClick={() => { setRenamingId(c.id); setRenameVal(c.name || ""); }}
                            data-testid={`rename-company-${c.id}`}
                            title="Renomear"
                            className="grid place-items-center w-7 h-7 rounded-lg text-slate-400 hover:text-slate-800 hover:bg-white opacity-0 group-hover/row:opacity-100 transition-opacity shrink-0"
                          >
                            <Pencil size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
                <div className="h-px bg-slate-100 my-1.5" />
                <button
                  onClick={() => { setOpenCo(false); onNewCompany(); }}
                  data-testid="new-company-btn"
                  className="w-full flex items-center gap-2 px-2.5 py-2 rounded-xl text-left text-[13px] font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <span className="grid place-items-center w-6 h-6 rounded-md bg-slate-100 text-slate-600"><Plus size={15} /></span>
                  Nova empresa
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {authed && (
        <div className="flex items-center gap-1 min-w-0">
          <nav className="flex items-center gap-0.5 overflow-x-auto scrollbar-none min-w-0">
            {link("home", "Novo", Plus)}
            {link("saved", "Orçamentos", FolderOpen)}
            {link("catalog", "Catálogo", Package)}
            {link("whatsapp", "WhatsApp", MessageCircle, waLocked)}
            {link("model", "Modelo", Settings2)}
          </nav>

          <SiteThemeToggle className="ml-1 shrink-0" />
          {planName === "free" && (
            <button onClick={() => onNav("upgrade")} data-testid="nav-upgrade-btn" className="cta-upgrade ml-1 sm:ml-2 h-8 px-3 sm:px-3.5 rounded-full text-[12.5px] font-semibold inline-flex items-center gap-1.5 shrink-0">
              <span className="hidden sm:inline">Fazer upgrade</span><span className="sm:hidden">Upgrade</span>
            </button>
          )}

          <div className="relative ml-1 shrink-0" ref={userRef}>
            <button onClick={() => setOpenUser((s) => !s)} data-testid="user-menu" className="grid place-items-center w-8 h-8 rounded-full ring-1 ring-slate-200 overflow-hidden bg-slate-100">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
              ) : (
                <span className="text-[12px] font-semibold text-slate-600">{(user.name || user.email || "?").slice(0, 1).toUpperCase()}</span>
              )}
            </button>
            {openUser && (
              <div className="absolute right-0 mt-2 w-64 bg-white ring-1 ring-slate-200 rounded-2xl p-1.5 shadow-xl z-50" data-testid="user-dropdown">
                <div className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <div className="text-[13px] font-semibold text-slate-900 truncate">{user.name || "Usuário"}</div>
                    <span data-testid="plan-badge" className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 ${planName === "free" ? "bg-slate-100 text-slate-500" : "bg-slate-900 text-white"}`}>
                      {planName !== "free" && <Crown size={10} />}{PLAN_META[planName]?.badge || "Free"}
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-400 truncate">{user.email}</div>
                  {subLine && <div className="text-[11px] text-slate-500 mt-1">{subLine}</div>}
                </div>
                <div className="h-px bg-slate-100 my-1" />
                {planName === "free" && (
                  <button onClick={() => { setOpenUser(false); onNav("upgrade"); }} data-testid="upgrade-btn" className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-left text-[13px] font-semibold text-slate-900 hover:bg-slate-50 transition-colors">
                    <Sparkles size={15} className="text-[#4A3BFA]" /> Fazer upgrade
                  </button>
                )}
                <button onClick={() => { setOpenUser(false); onNav("subscription"); }} data-testid="manage-subscription-btn" className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-left text-[13px] font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                  <CreditCard size={15} className="text-slate-500" /> Gerenciar assinatura
                </button>
                <div className="h-px bg-slate-100 my-1" />
                <button onClick={() => { setOpenUser(false); onLogout(); }} data-testid="logout-btn" className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-left text-[13px] font-medium text-rose-600 hover:bg-rose-50 transition-colors">
                  <LogOut size={15} /> Sair
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
